import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Link2Off, RefreshCw, Search, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FichaColaborador } from "@/components/FichaColaborador";
import { AcessosSemFicha } from "@/components/AcessosSemFicha";
import { trpc } from "@/lib/trpc";
import { formatarData } from "@/lib/utils";
import { ROTULO_TIPO_PLURAL, rotuloTipo } from "@/lib/tipoProfissional";

// ============================================================
// R2 (08/09) — Colaboradores
// ============================================================
// A lista de gente do grupo inteiro, em um lugar só, com o semáforo do
// cadastro à vista. Quem monta a lista é o escalas (é dele a tabela de
// profissionais e a coluna `setor`); quem responde pelo cadastro fiscal, pelos
// documentos e pelos contratos é o ERP da clínica — esta tela só pergunta.
//
// Duas pendências aparecem em vermelho de propósito, porque as duas travam
// documento lá na frente: quem não tem VÍNCULO com a clínica (sem ele não
// existe ficha do outro lado) e quem tem CHECKLIST aberto na modalidade.
//
// 25/09/2026: no topo, o bloco "Acessos sem ficha" (components/AcessosSemFicha)
// — quem tem login e ainda não tem linha aqui; antes essa gente não aparecia.

const ROTULO_SETOR: Record<string, string> = {
  clinica: "Clínica",
  agro: "Agro",
  bt: "Banho & Tosa",
};

// Mesmos rótulos do ERP (checklist-colaborador.ts, MODALIDADES).
const ROTULO_MODALIDADE: Record<string, string> = {
  clt: "CLT",
  pf_autonomo: "PF autônomo",
  pj: "PJ",
  socio: "Sócio",
  estagio: "Estágio",
  freela: "Freela (eventual)",
};

/** Busca que ignora acento e caixa: "taissa" acha "Taíssa". */
function simplificar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function dataBr(iso: string | null | undefined): string {
  return iso ? formatarData(iso) : "—";
}

export function Colaboradores() {
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState("");
  const [setor, setSetor] = useState<string>("todos");
  const [modalidade, setModalidade] = useState<string>("todas");
  // 13/10/2026: filtro por tipo da ficha (secretárias / motorista = `apoio`).
  const [tipo, setTipo] = useState<string>("todos");
  const [incluirInativos, setIncluirInativos] = useState(false);

  const q = trpc.colaboradores.listar.useQuery({ incluirInativos });

  const abertoId = params.get("id") ? Number(params.get("id")) : null;
  function abrir(id: number | null) {
    const novo = new URLSearchParams(params);
    if (id === null) novo.delete("id");
    else novo.set("id", String(id));
    setParams(novo, { replace: true });
  }

  const itens = q.data?.itens ?? [];
  const totais = q.data?.totais;

  const filtrados = useMemo(() => {
    const alvo = simplificar(busca);
    return itens.filter((i) => {
      if (setor !== "todos") {
        if (setor === "sem" ? i.setor !== null : i.setor !== setor) return false;
      }
      if (modalidade !== "todas") {
        const m = i.clinica?.modalidade ?? null;
        if (modalidade === "sem" ? m !== null : m !== modalidade) return false;
      }
      if (tipo !== "todos" && i.tipo !== tipo) return false;
      if (alvo === "") return true;
      const texto = [i.nome, i.userEmail ?? "", i.clinica?.cargo ?? "", i.clinica?.cpf ?? "", i.clinica?.cnpj ?? ""].join(" ");
      return simplificar(texto).includes(alvo);
    });
  }, [itens, busca, setor, modalidade, tipo]);

  const nomeAberto = itens.find((i) => i.id === abertoId)?.nome ?? "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Colaboradores</h1>
          <p className="mt-1 text-sm text-slate-500">
            Ficha do colaborador por setor: dados cadastrais, checklist da modalidade e documentos.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <AcessosSemFicha />

      {q.data && !q.data.canalClinica && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            O canal com a clínica não está configurado neste ambiente. A lista de pessoas aparece,
            mas cadastro fiscal, checklist e documentos ficam em branco.
          </span>
        </div>
      )}

      {(totais?.semVinculo ?? 0) > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <Link2Off className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>{totais!.semVinculo}</strong>{" "}
            {totais!.semVinculo === 1 ? "pessoa sem vínculo" : "pessoas sem vínculo"} com o cadastro
            da clínica. Sem o de-para não existe ficha fiscal, contrato nem documento para elas —{" "}
            <Link className="font-medium underline" to="/admin/vinculos">
              resolver em Vínculos
            </Link>
            .
          </span>
        </div>
      )}

      {(totais?.semFicha ?? 0) > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {totais!.semFicha} ficha(s) não vieram da clínica nesta consulta. As linhas aparecem sem
            cadastro; tente atualizar.
          </span>
        </div>
      )}

      {/* ── Filtros ─────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="min-w-56 flex-1">
            <label className="text-xs font-medium text-slate-600" htmlFor="busca">
              Buscar
            </label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="busca"
                className="pl-9"
                placeholder="Nome, e-mail, cargo, CPF ou CNPJ"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600" htmlFor="filtro-setor">
              Setor
            </label>
            <select
              id="filtro-setor"
              className="mt-1 flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={setor}
              onChange={(e) => setSetor(e.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="clinica">Clínica</option>
              <option value="agro">Agro</option>
              <option value="bt">Banho &amp; Tosa</option>
              <option value="sem">Sem setor</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600" htmlFor="filtro-modalidade">
              Modalidade
            </label>
            <select
              id="filtro-modalidade"
              className="mt-1 flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={modalidade}
              onChange={(e) => setModalidade(e.target.value)}
            >
              <option value="todas">Todas</option>
              <option value="clt">CLT</option>
              <option value="pf_autonomo">PF autônomo</option>
              <option value="pj">PJ</option>
              <option value="socio">Sócio</option>
              <option value="estagio">Estágio</option>
              <option value="freela">Freela (eventual)</option>
              <option value="sem">Sem modalidade</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600" htmlFor="filtro-tipo">
              Tipo
            </label>
            <select
              id="filtro-tipo"
              className="mt-1 flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="veterinario">{ROTULO_TIPO_PLURAL.veterinario}</option>
              <option value="auxiliar">{ROTULO_TIPO_PLURAL.auxiliar}</option>
              <option value="ambos">{ROTULO_TIPO_PLURAL.ambos}</option>
              <option value="apoio">{ROTULO_TIPO_PLURAL.apoio}</option>
            </select>
          </div>

          <label className="flex h-10 items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={incluirInativos}
              onChange={(e) => setIncluirInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
        </CardContent>
      </Card>

      {totais && (
        <p className="text-xs text-slate-500">
          {filtrados.length} de {totais.pessoas} pessoa(s) · {totais.comPendencia} com pendência de
          cadastro · {totais.semSetor} sem setor definido.
        </p>
      )}

      {/* ── Lista ───────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="px-4 py-10 text-center text-sm text-slate-500">Carregando…</p>
          ) : filtrados.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-500">
              Ninguém com esses filtros.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Setor</TableHead>
                    <TableHead>Modalidade</TableHead>
                    <TableHead>Contratante</TableHead>
                    <TableHead>Admissão</TableHead>
                    <TableHead>Cadastro</TableHead>
                    <TableHead>Contrato</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtrados.map((i) => {
                    const pendencias = i.clinica?.checklist.totalPendencias ?? 0;
                    return (
                      <TableRow key={i.id} className={i.semVinculo ? "bg-red-50/40" : undefined}>
                        <TableCell>
                          <div className="flex items-start gap-2">
                            <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900">
                                {i.nome}
                                {!i.ativo && <span className="ml-2 text-xs text-slate-400">inativo</span>}
                              </p>
                              <p className="text-xs text-slate-500">
                                {i.clinica?.cargo || rotuloTipo(i.tipo)}
                                {i.userEmail ? ` · ${i.userEmail}` : " · sem login"}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {i.setor ? (
                            <Badge variant="default">{ROTULO_SETOR[i.setor]}</Badge>
                          ) : (
                            <span className="text-xs text-slate-400">sem setor</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {i.clinica?.modalidade ? (
                            <Badge variant="outline">
                              {ROTULO_MODALIDADE[i.clinica.modalidade] ?? i.clinica.modalidade}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {i.clinica?.empresaCnpj ?? "—"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {dataBr(i.clinica?.dataAdmissao ?? null)}
                        </TableCell>
                        <TableCell>
                          {i.semVinculo ? (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                              sem vínculo
                            </span>
                          ) : !i.clinica ? (
                            <span className="text-xs text-slate-400">{i.erroClinica ? "erro" : "—"}</span>
                          ) : pendencias > 0 ? (
                            <span
                              title={i.clinica.checklist.resumo}
                              className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800"
                            >
                              {pendencias} pendência{pendencias > 1 ? "s" : ""}
                            </span>
                          ) : (
                            <Badge variant="ativo">completo</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {!i.clinica ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : i.clinica.contrato.assinado ? (
                            <Badge variant="ativo">assinado</Badge>
                          ) : i.clinica.contrato.ultimoStatus ? (
                            <Badge variant="outline">{i.clinica.contrato.ultimoStatus}</Badge>
                          ) : (
                            <span className="text-xs text-slate-400">nenhum</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => abrir(i.id)}>
                            Abrir ficha
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Ficha ───────────────────────────────────────────────────── */}
      <Dialog open={abertoId !== null} onOpenChange={(aberto) => !aberto && abrir(null)}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ficha do colaborador</DialogTitle>
            <DialogDescription>
              {nomeAberto ? `${nomeAberto} — ` : ""}cadastro fiscal, documentos e contratos vivem no
              sistema da clínica; o setor vive aqui.
            </DialogDescription>
          </DialogHeader>
          {abertoId !== null && <FichaColaborador profissionalId={abertoId} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
